import { Alert, Button, Card, DatePicker, Modal, Radio, Typography } from "antd";
import CopyInput from "components/atoms/CopyInput";
import AbsoluteRelativeDatePicker from "components/molecules/AbsoluteRelativeDatePicker";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";

interface IShareDataObjectModalDialogProps {
    show: boolean;
    onGetLink: ({ readOnly, expiryDate }: { readOnly: boolean; expiryDate: Dayjs }) => void;
    shareLink: string | null;
    loading: boolean;
    onClose: () => void;
}

function ShareDataObjectModalDialog({ show, onGetLink, shareLink, loading, onClose }: IShareDataObjectModalDialogProps) {
    const [modalVisible, setModalVisible] = useState(show);
    const [activeTabKey, setActiveTabKey] = useState<string>("ABSOLUTE_DATE");
    const [readOnly, setReadOnly] = useState(true);
    const [expiryDate, setExpiryDate] = useState<Dayjs | null>(null);

    useEffect(() => {
        setModalVisible(show);
    }, [show]);

    const closeModal = () => {
        setModalVisible(false);
    };

    const onTabChange = (key: string) => {
        setActiveTabKey(key);
    };

    const onExpiryDateChange = (value: Dayjs | null) => {
        setExpiryDate(value);
    };

    const onGetLinkClick = () => {
        onGetLink && expiryDate && onGetLink({ readOnly, expiryDate });
    };

    return (
        <Modal
            title="Dataset record share"
            centered={true}
            open={modalVisible}
            onCancel={closeModal}
            afterClose={onClose}
            classNames={{
                body: "gap-2 flex flex-col h-[500px]",
            }}
            destroyOnHidden={true}
            footer={null}
        >
            <Card
                tabList={[
                    {
                        key: "ABSOLUTE_DATE",
                        tab: <Typography.Title level={5}>Expiry Date</Typography.Title>,
                    },
                    {
                        key: "RELATIVE",
                        tab: <Typography.Title level={5}>Expiry Duration</Typography.Title>,
                    },
                ]}
                activeTabKey={activeTabKey}
                onTabChange={(key) => {
                    onTabChange(key);
                }}
                size="small"
                className="flex-1"
            >
                {activeTabKey === "ABSOLUTE_DATE" ? (
                    <>
                        <Alert title="The link will expire at the set date and time." type="warning" showIcon className="mb-4" />
                        <DatePicker showTime value={expiryDate} onChange={onExpiryDateChange} className="w-full" />
                    </>
                ) : activeTabKey === "RELATIVE" ? (
                    <>
                        <Alert title="The link will expire after the set duration." type="warning" showIcon className="mb-4" />
                        <AbsoluteRelativeDatePicker onChange={onExpiryDateChange}></AbsoluteRelativeDatePicker>
                    </>
                ) : (
                    <></>
                )}
            </Card>
            {expiryDate && <Alert type="info" title={`Expires on ${expiryDate.format("YYYY-MM-DD HH:mm:ss")}`} />}
            <Card title="Link Permission" size="small">
                <Radio.Group onChange={(e) => setReadOnly(e.target.value === "readOnly")} value={readOnly ? "readOnly" : "readWrite"}>
                    <Radio value="readOnly">Read Only</Radio>
                    <Radio value="readWrite">Read/Write</Radio>
                </Radio.Group>
            </Card>
            <Card size="small">
                <div className="share-datarecord-sharelink flex w-full gap-2">
                    <CopyInput value={shareLink} readOnly={true} copyToolTip={"Copy link"} className="flex-1"></CopyInput>
                    <Button type="primary" loading={loading} onClick={onGetLinkClick} disabled={!expiryDate}>
                        Get link
                    </Button>
                </div>
            </Card>
        </Modal>
    );
}

export default ShareDataObjectModalDialog;
